/**
 * Runtime helpers of the automation interpreter (deterministic: they run in Temporal workflow code):
 * context paths, conditions, Map metadata steps and `{{path}}` templates.
 */
import type { Condition, Mapping } from './automation.ts';

export function getPath(ctx: unknown, path: string): unknown {
  let cur: unknown = ctx;
  for (const part of path.split('.')) {
    if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

export function evaluateCondition(c: Condition, ctx: unknown): boolean {
  const v = getPath(ctx, c.path);
  switch (c.op) {
    case 'exists': return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length);
    case 'not_exists': return v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
    case 'equals': return Array.isArray(v) ? v.includes(c.value as never) : String(v) === String(c.value);
    case 'not_equals': return Array.isArray(v) ? !v.includes(c.value as never) : String(v) !== String(c.value);
    case 'contains': return Array.isArray(v) ? v.map(String).includes(String(c.value)) : String(v ?? '').toLowerCase().includes(String(c.value ?? '').toLowerCase());
    case 'greater_than': return Number(v) > Number(c.value);
    case 'less_than': return Number(v) < Number(c.value);
    case 'has_master': return hasMaster(v, c.master);
    case 'no_master': return !hasMaster(v, c.master);
  }
}

/**
 * One master in the run context (1.2.0): asset.masters is the current version's masters (the primary first), and
 * master is the new master of a Master added run. Templates show them as {{master.variant}}, {{master.format}},
 * {{master.label}} and {{asset.masters}} (the labels, comma separated).
 */
export interface RunContextMaster {
  id: string;
  primary: boolean;
  variantId: string;
  /** The variant's name ({{master.variant}}) and code. */
  variant: string;
  variantCode: string;
  formatClass: string;
  /** "UHD 2160p25 · ProRes 422 HQ · 8 ch" ({{master.format}}). */
  format: string;
  /** "Textless · UHD 2160p25 · ProRes 422 HQ" ({{master.label}}). */
  label: string;
}

/** Whether a list of RunContextMaster holds one of the variant and format class asked for (null or absent: any). */
function hasMaster(v: unknown, m: { variantId: string | null; format: string | null } | undefined): boolean {
  if (!Array.isArray(v)) return false;
  return v.some((x) => {
    if (!x || typeof x !== 'object') return false;
    const r = x as Partial<RunContextMaster>;
    return (!m?.variantId || r.variantId === m.variantId) && (!m?.format || r.formatClass === m.format);
  });
}

/** Apply one mapping to the run context: the value to set, or null to leave the field alone. */
export function applyMapping(m: Mapping, ctx: unknown): string | null {
  let v = renderTemplate(m.from, ctx).trim() === '' ? '' : renderTemplate(m.from, ctx);
  if (v === '') v = m.fallback;
  if (v === '') return null;
  if (Object.prototype.hasOwnProperty.call(m.lookup, v.trim())) v = m.lookup[v.trim()]!;
  switch (m.transform) {
    case 'trim': return v.trim();
    case 'upper': return v.toUpperCase();
    case 'lower': return v.toLowerCase();
    case 'capitalize': return v.trim().toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase());
    case 'first_line': return v.split(/\r?\n/)[0]!.trim();
    case 'number': {
      const n = Number(v.replace(/[^0-9.,-]/g, '').replace(',', '.'));
      return Number.isFinite(n) ? String(n) : null;
    }
    case 'date': {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    default: return v;
  }
}

/**
 * A value as template text: lists comma separated, and an object with a label (a RunContextMaster, so
 * {{asset.masters}} is "Programme · UHD 2160p25 · ProRes 422 HQ, Textless · HD 1080p25 · H.264 High") by its label.
 */
function templateText(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.map(templateText).join(', ');
  if (typeof v === 'object') {
    const label = (v as { label?: unknown }).label;
    return typeof label === 'string' ? label : JSON.stringify(v);
  }
  return String(v);
}

/**
 * Replace {{path}} placeholders with context values ({{master.variant}}, {{master.format}}, {{master.label}}, {{asset.masters}} since 1.2.0).
 *
 * 1.6.0: template functions (TEMPLATE_FUNCTIONS) are calls, `{{name arg …}}`, each argument a path, a number, true,
 * false, null or a quoted string. {{json path}} is replaced here by the value as JSON. A signed-link function is bound,
 * not resolved: its arguments are read from the context now (deterministically, in workflow code) and it becomes a
 * token `{{@name [args]}}` that the webhook activity resolves through the API (templateCalls, applyTemplateCalls),
 * where the link is signed, checked against the endpoint's "may receive media links" and logged. A function name used
 * as a path ({{proxyUrl}}) is a call without arguments, never a context path.
 */
export function renderTemplate(template: string, ctx: unknown): string {
  return template
    .replace(CALL_RE, (match: string, name: string, rawArgs: string | undefined) => bindCall(match, name, rawArgs, ctx))
    .replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, p: string) => templateText(getPath(ctx, p)));
}

/* ---------------------------------------------------------------- 1.6.0 template functions */

/**
 * The template functions (1.6.0, stream A resolves the links: docs/dev/mcp-1.6.0-build.md). Every `link` function
 * makes a signed, time-limited URL for the run's asset only (TTL: the step's mediaLinkTtlSeconds, default 1 h, at
 * most 24 h), refused to endpoints not marked "may receive media links"; each link's use is logged.
 *  - shotFrameUrl(shotIndex, position?): a frame of the shot (0-based index on the Shots layer) as a JPEG, 768 px on
 *    its long side; position 'start' | 'middle' (default) | 'end';
 *  - proxyUrl(): the current version's proxy (video or audio);
 *  - keyframeUrl(seconds): a frame at that time (seconds from the start of the proxy), 768 px;
 *  - exportFileUrl(exportId): an export's main file (an Export asset step's output.exportId);
 *  - subtitleUrl(lang): the current version's subtitles in that language as WebVTT;
 *  - base64Frame(seconds): the frame at that time as base64 JPEG text (inline images; counts against the 1 MB body);
 *  - json(value): the value as JSON (not a link; resolved in the workflow).
 */
export const TEMPLATE_FUNCTIONS = {
  shotFrameUrl: { args: ['shotIndex', 'position?'], link: true, label: 'Link to a frame of a shot' },
  proxyUrl: { args: [], link: true, label: 'Link to the proxy' },
  keyframeUrl: { args: ['seconds'], link: true, label: 'Link to a frame at a time' },
  exportFileUrl: { args: ['exportId'], link: true, label: 'Link to an export’s file' },
  subtitleUrl: { args: ['lang'], link: true, label: 'Link to subtitles (WebVTT)' },
  base64Frame: { args: ['seconds'], link: true, label: 'A frame as base64 JPEG' },
  json: { args: ['value'], link: false, label: 'A value as JSON' },
} as const;
export type TemplateFunctionName = keyof typeof TEMPLATE_FUNCTIONS;
export type LinkFunctionName = Exclude<TemplateFunctionName, 'json'>;
export const LINK_FUNCTION_NAMES = (Object.keys(TEMPLATE_FUNCTIONS) as TemplateFunctionName[]).filter((k) => TEMPLATE_FUNCTIONS[k].link) as LinkFunctionName[];
export type TemplateArg = string | number | boolean | null;

/** A bound link call in rendered text, for the activity to resolve. */
export interface BoundTemplateCall {
  name: LinkFunctionName;
  args: TemplateArg[];
  /** The exact text to replace. */
  token: string;
}

/** Signed media links' lifetime (1.6.0): default 1 h, at most 24 h. */
export const MEDIA_LINK_TTL = { defaultSeconds: 3600, minSeconds: 60, maxSeconds: 24 * 3600 } as const;
/** Call webhook with a JSON body (1.6.0): the most of its answer that is read, and its default time to answer. */
export const WEBHOOK_JSON_MAX_RESPONSE_BYTES = 1024 * 1024;
export const WEBHOOK_DEFAULT_TIMEOUT_SECONDS = 15;
/** Frames for vision models (shotFrameUrl, keyframeUrl, base64Frame, get_shot_frames): JPEG, this long on the long side. */
export const MEDIA_FRAME_LONG_SIDE = 768;

const CALL_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9]*)((?:\s+(?:"[^"{}]*"|'[^'{}]*'|[^\s{}]+))*)\s*\}\}/g;
/** A bound call's token; the name may hold digits (base64Frame). */
const BOUND_RE = /\{\{@([a-zA-Z][a-zA-Z0-9]*) (\[[^{}]*\])\}\}/g;
const ARG_RE = /"[^"{}]*"|'[^'{}]*'|[^\s{}]+/g;

const isFunctionName = (name: string): name is TemplateFunctionName => Object.prototype.hasOwnProperty.call(TEMPLATE_FUNCTIONS, name);

function argValue(raw: string, ctx: unknown): unknown {
  if ((raw.startsWith('"') && raw.endsWith('"') && raw.length > 1) || (raw.startsWith("'") && raw.endsWith("'") && raw.length > 1)) return raw.slice(1, -1);
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  return getPath(ctx, raw);
}

const scalar = (v: unknown): TemplateArg => (v === undefined || v === null ? null : typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? v : templateText(v));

function bindCall(match: string, name: string, rawArgs: string | undefined, ctx: unknown): string {
  if (!isFunctionName(name)) return match; // a plain {{path}}: the next pass
  const args = (rawArgs?.match(ARG_RE) ?? []).map((a) => argValue(a, ctx));
  if (name === 'json') return JSON.stringify(args[0] ?? null);
  // Only scalars, and no braces in the token: `{` and `}` occur only inside strings there, escaped as { / }.
  const json = JSON.stringify(args.map(scalar)).replace(/\{/g, '\\u007b').replace(/\}/g, '\\u007d');
  return `{{@${name} ${json}}}`;
}

/** The bound link calls in rendered text (renderTemplate's tokens), in order, each token once. */
export function templateCalls(text: string): BoundTemplateCall[] {
  const out: BoundTemplateCall[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(BOUND_RE)) {
    const name = m[1]!;
    if (!isFunctionName(name) || !TEMPLATE_FUNCTIONS[name].link || seen.has(m[0])) continue;
    let args: TemplateArg[];
    try { args = JSON.parse(m[2]!) as TemplateArg[]; } catch { continue; }
    seen.add(m[0]);
    out.push({ name: name as LinkFunctionName, args, token: m[0] });
  }
  return out;
}

/** Replace the bound calls with what they resolved to (a token not in `resolved` becomes empty text). */
export function applyTemplateCalls(text: string, resolved: ReadonlyMap<string, string>): string {
  return text.replace(BOUND_RE, (token: string) => resolved.get(token) ?? '');
}

/** Whether a template uses a link function ({{shotFrameUrl …}}, {{proxyUrl}}, …), before or after binding. */
export function isLinkTemplateUsed(text: string): boolean {
  for (const m of text.matchAll(CALL_RE)) if (isFunctionName(m[1]!) && TEMPLATE_FUNCTIONS[m[1] as TemplateFunctionName].link) return true;
  return templateCalls(text).length > 0;
}

/* ---------------------------------------------------------------- 1.6.0 JSON bodies */

export type BodyJsonParse = { ok: true; value: unknown } | { ok: false; error: string };

/**
 * Call webhook's bodyJson (1.6.0): JSON whose string values may hold templates; a value may also be a bare
 * {{json path}} (outside quotes), or a string that is only "{{json path}}", to insert that value as JSON (an array, an
 * object). Any other template outside a string is refused. Checked when the flow is published.
 */
export function parseBodyJson(text: string): BodyJsonParse {
  let out = '';
  let inString = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (inString) {
      out += c;
      if (c === '\\') { out += text[i + 1] ?? ''; i++; continue; }
      if (c === '"') inString = false;
      continue;
    }
    if (c === '"') { inString = true; out += c; continue; }
    if (c === '{' && text[i + 1] === '{') {
      const end = text.indexOf('}}', i + 2);
      if (end < 0) return { ok: false, error: `An unclosed {{ at character ${i + 1}` };
      const inner = text.slice(i + 2, end).trim();
      if (!/^json\s+[a-zA-Z0-9_.]+$/.test(inner)) return { ok: false, error: `Only {{json …}} may stand outside quotes (character ${i + 1}); put other templates inside a string` };
      out += JSON.stringify(`{{${inner}}}`);
      i = end + 1;
      continue;
    }
    out += c;
  }
  try {
    return { ok: true, value: JSON.parse(out) };
  } catch (err) {
    return { ok: false, error: `Not valid JSON: ${(err as Error).message}` };
  }
}

const WHOLE_JSON_RE = /^\{\{\s*json\s+([a-zA-Z0-9_.]+)\s*\}\}$/;

/**
 * A parsed bodyJson with the run context filled in: each string rendered with renderTemplate (links bound, not
 * resolved), and a string that is only {{json path}} replaced by the value itself. Keys are not templated.
 */
export function renderBodyJson(value: unknown, ctx: unknown): unknown {
  if (typeof value === 'string') {
    const whole = WHOLE_JSON_RE.exec(value);
    if (whole) {
      const v = getPath(ctx, whole[1]!);
      return v === undefined ? null : JSON.parse(JSON.stringify(v));
    }
    return renderTemplate(value, ctx);
  }
  if (Array.isArray(value)) return value.map((v) => renderBodyJson(v, ctx));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, renderBodyJson(v, ctx)]));
  return value;
}

/** Every string in a rendered body mapped (the activity resolves the bound links in each: templateCalls). */
export function mapBodyStrings(value: unknown, fn: (s: string) => string): unknown {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map((v) => mapBodyStrings(v, fn));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapBodyStrings(v, fn)]));
  return value;
}

/* ---------------------------------------------------------------- 1.6.0 For each */

/** For each (1.6.0): items at a time and per run. */
export const FOR_EACH_LIMITS = { defaultConcurrency: 2, maxConcurrency: 8, maxItems: 500 } as const;

/**
 * One item of a For each step, as {{item.*}} in its body (For each shot also as {{shot.*}}). Ticks and seconds are on
 * the proxy's time line (0 at its first frame), timecodes in the asset's source timecode.
 */
export interface ForEachItem {
  /** 0-based (shotFrameUrl takes it); `number` is 1-based, for text. */
  index: number;
  number: number;
  /** How many items the run has (after maxItems). */
  count: number;
  startTicks: number;
  endTicks: number;
  startSeconds: number;
  endSeconds: number;
  middleSeconds: number;
  durationSeconds: number;
  startTimecode: string;
  endTimecode: string;
  /** For a shot: the transcript's and subtitles' text inside it. For a segment: its text. */
  text: string;
  /** For a segment: its values; [] for a shot. */
  values: string[];
  /** For a segment: its id; for a shot: null. */
  segmentId: string | null;
  /** For a segment: its layer; for a shot: null. */
  stratumId: string | null;
}

/**
 * Each For each step's body (1.6.0): the steps reached from its `each` edges, through any port. Graph rules
 * (forEachProblems, automation.ts) keep bodies closed. The interpreter leaves body steps to their For each step.
 */
export function forEachBodies(graph: { nodes: ReadonlyArray<{ id: string; type: string }>; edges: ReadonlyArray<{ from: string; to: string; port?: string }> }): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const n of graph.nodes) {
    if (n.type !== 'for_each_shot' && n.type !== 'for_each_segment') continue;
    const body = new Set<string>();
    const stack = graph.edges.filter((e) => e.from === n.id && e.port === 'each').map((e) => e.to);
    while (stack.length) {
      const id = stack.pop()!;
      if (body.has(id)) continue;
      body.add(id);
      for (const e of graph.edges) if (e.from === id && !body.has(e.to)) stack.push(e.to);
    }
    out.set(n.id, body);
  }
  return out;
}

/** Every step inside some For each body (the interpreter's main loop leaves them to their For each step). */
export function forEachBodyNodes(graph: Parameters<typeof forEachBodies>[0]): Set<string> {
  const all = new Set<string>();
  for (const body of forEachBodies(graph).values()) for (const id of body) all.add(id);
  return all;
}

/**
 * 1.8.0 rights: whether a Call webhook step asks for an analysis (AI description) rather than delivering the asset.
 * Such a step is not rights-gated; every other Call webhook step delivers and is.
 *
 * 1.8.1: only when its answer is written back into PRISM as suggestions: an Update segment step, or Update metadata
 * with asSuggestion, reached from it whose settings use its answer (`nodes.<its id>.output…`). Being inside a For each
 * body no longer counts, nor does a suggestion step that ignores the answer.
 */
export function isAnalysisWebhookStep(
  graph: { nodes: ReadonlyArray<{ id: string; type: string; asSuggestion?: boolean }>; edges: ReadonlyArray<{ from: string; to: string; port?: string }> },
  nodeId: string,
): boolean {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node || node.type !== 'call_webhook') return false;
  const uses = new RegExp(`nodes\\.${nodeId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.output(?![A-Za-z0-9_])`);
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const seen = new Set<string>([nodeId]);
  const stack = graph.edges.filter((e) => e.from === nodeId).map((e) => e.to);
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const n = byId.get(id);
    if ((n?.type === 'update_segment' || (n?.type === 'update_metadata' && n.asSuggestion)) && uses.test(JSON.stringify(n))) return true;
    for (const e of graph.edges) if (e.from === id && !seen.has(e.to)) stack.push(e.to);
  }
  return false;
}
